import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="About Ogaba Joseph Attah | Founder & Digital Intelligence Leader"
        description="Ogaba Joseph Attah is a technology founder and digital infrastructure intelligence leader focused on building systems that enable better decisions at scale."
        canonicalUrl="https://www.ogabajosephattah.com/about-ogaba-joseph-attah"
      />
      
      <Navigation />
      
      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="about-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  Biography
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-8" data-testid="about-title">
                  About Ogaba Joseph Attah
                </h1>
                <div className="prose prose-lg dark:prose-invert">
                  <p className="text-lg leading-relaxed text-muted-foreground mb-6">
                    Ogaba Joseph Attah is a technology founder and digital infrastructure intelligence leader focused on building systems that enable better decisions at scale.
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground mb-6">
                    His career spans more than a decade of hands-on leadership in telecommunications infrastructure, analytics platforms, and intelligence-driven digital systems. He has led the design and delivery of carrier-grade connectivity, data-driven platforms, and scalable architectures used across public and private sector environments.
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    Working across Africa and the United Kingdom, Ogaba collaborates with governments, enterprises, startups, and ecosystem partners to strengthen digital capacity, infrastructure resilience, and long-term economic impact.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative lg:sticky lg:top-32"
              >
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-muted">
                  <img
                    src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/smh6391z_One%20Community%20Team%20Headshot0171.jpg"
                    alt="Ogaba Joseph Attah"
                    className="w-full h-full object-cover"
                    data-testid="about-image"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="philosophy-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                Professional Philosophy
              </p>
              <div className="relative">
                <Quote className="absolute -top-2 -left-4 h-12 w-12 text-primary/20" />
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight pl-8">
                  Digital infrastructure does not have a data problem. It is an intelligence problem.
                </blockquote>
              </div>
              <p className="text-lg text-muted-foreground mt-8 pl-8">
                Real impact comes from understanding systems — how networks interact, where decisions fail, and where investment creates meaningful outcomes. This philosophy underpins all of Ogaba's work across connectivity, analytics, and infrastructure intelligence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Experience Highlights */}
        <section className="py-24 md:py-32" data-testid="experience-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-16"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                Experience
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Areas of Impact
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Telecommunications Infrastructure',
                  description: 'Over a decade leading design and delivery of carrier-grade connectivity solutions across multiple markets.'
                },
                {
                  title: 'Analytics Platforms',
                  description: 'Building data-driven platforms that transform fragmented information into actionable intelligence.'
                },
                {
                  title: 'Intelligence-Driven Systems',
                  description: 'Creating scalable architectures used across public and private sector environments for decision-making.'
                },
                {
                  title: 'Cross-Border Collaboration',
                  description: 'Working with governments, enterprises, and ecosystem partners across Africa and the United Kingdom.'
                }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 border-l-2 border-primary/30 hover:border-primary transition-colors"
                  data-testid={`experience-item-${index}`}
                >
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="about-cta">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Explore the Companies
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Learn more about Nexintell Limited, Infralytics-GIIS, and Evolve Digital Africa.
              </p>
              <Button asChild size="lg" data-testid="about-companies-cta">
                <Link to="/companies">
                  View Companies & Ventures
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
